// src/features/departments/pages/CreateDepartmentPage/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';

// Hooks
import { generateRandomPassword } from '../../../utils/random';
import { useDepartments } from '../hooks/useDepartment';
import { useModules } from '../../../hooks/api/useModules';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { FormPage, FormSection } from '../../../components/common/form';

function getCryptoRandomBetween(min, max){
  var MAX_VAL = 4294967295;
  var numberOfRandomsNeeded = Math.ceil((max - min) / MAX_VAL);
  var cryptoRandomNumbers = new Uint32Array(numberOfRandomsNeeded);
  crypto.getRandomValues(cryptoRandomNumbers);
  
  for(var i = 0, sum = 0; i < cryptoRandomNumbers.length; i++){
    sum += cryptoRandomNumbers[i];
  }
  
  var randomDecimal = sum / (MAX_VAL * numberOfRandomsNeeded);
  return randomDecimal === 1 ? getCryptoRandomBetween(min, max) : Math.floor(randomDecimal * (max - min + 1) + min);
}

function getRandomChar(str){
  return str.charAt(getCryptoRandomBetween(0, str.length - 1));
}

String.random = function(length, characters) {
  for(var i = 0, str = ""; i < length; i++) str += getRandomChar(characters);
  return str;
};

export default function CreateDepartmentPage() {
  const { t } = useTranslation(['departments']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    master_username: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedModules, setSelectedModules] = useState([]);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const [createdDepartment, setCreatedDepartment] = useState(null);
  
  // API hooks
  const {
    loading: departmentsLoading,
    createDepartment,
    enableModule,
  } = useDepartments();

  const {
    modules,
    loading: modulesLoading,
    fetchAllModules,
  } = useModules();

  useEffect(() => {
    fetchAllModules();
  }, []);

  const steps = [
    t('departments:form.create_title'),
    t('departments:actions.manage_modules'),
    t('departments:form.master_password_generated')
  ];

  const validateStep1 = useCallback(() => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = t('departments:form.name_required');
    } else if (formData.name.length > 100) {
      errors.name = t('departments:form.name_max_length');
    }
    
    if (!formData.master_username.trim()) {
      errors.master_username = t('departments:form.username_required');
    } else if (formData.master_username.length > 50) {
      errors.master_username = t('departments:form.username_max_length');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleFormChange = useCallback((field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  const handleModuleToggle = useCallback((moduleId) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  }, []);

  const generatePassword = () => {
    return generateRandomPassword(8, "0123456789");
  };

  const handleNext = useCallback(async () => {
    if (activeStep === 0) {
      if (!validateStep1()) return;
      
      try {
        const password = generatePassword();
        setGeneratedPassword(password);
        
        const result = await createDepartment({
          ...formData,
          master_password: password
        });
        
        if (result.success) {
          setCreatedDepartment(result.data);
          setActiveStep(1);
        } else {
          showError(result.error || t('departments:errors.create_failed'));
        }
      } catch (error) {
        showError(t('departments:errors.create_failed'));
      }
    } else if (activeStep === 1) {
      // Enable selected modules
      try {
        for (const moduleId of selectedModules) {
          await enableModule(createdDepartment.department_id, moduleId);
        }
        setActiveStep(2);
      } catch (error) {
        showError(t('departments:errors.module_enable_failed'));
      }
    }
  }, [activeStep, validateStep1, formData, createDepartment, enableModule, selectedModules, createdDepartment, showError, t]);

  const handleBack = useCallback(() => {
    if (activeStep === 1) {
      // Can't go back after creating department
      return;
    }
    setActiveStep(prev => prev - 1);
  }, [activeStep]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generatedPassword]);

  const handleFinish = useCallback(() => {
    showSuccess(t('departments:success.created'));
    navigate('/departments');
  }, [navigate, showSuccess, t]);

  const handleCancel = useCallback(() => {
    navigateBack('/departments');
  }, [navigateBack]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('departments:subtitle')}
            </Typography>
            
            <TextField
              autoFocus
              fullWidth
              label={t('departments:labels.name')}
              value={formData.name}
              onChange={handleFormChange('name')}
              error={!!formErrors.name}
              helperText={formErrors.name}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label={t('departments:labels.master_username')}
              value={formData.master_username}
              onChange={handleFormChange('master_username')}
              error={!!formErrors.master_username}
              helperText={formErrors.master_username}
              margin="normal"
              required
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('departments:form.password_auto_generated')}
            </Alert>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('departments:modules.dialog_subtitle')}
            </Typography>
            
            {modulesLoading ? (
              <Typography>Loading modules...</Typography>
            ) : (
              <List>
                {modules.map((module) => {
                  const isSelected = selectedModules.includes(module.module_id);
                  
                  return (
                    <ListItem key={module.module_id} divider>
                      <ListItemIcon>
                        <ExtensionIcon color={isSelected ? 'primary' : 'disabled'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={module.name}
                        secondary={module.description}
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={isSelected}
                          onChange={() => handleModuleToggle(module.module_id)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('departments:success.created')}
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('departments:form.master_password_generated')}
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'grey.100', 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3
              }}
            >
              <Box display="flex" gap={3}>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {generatedPassword.slice(0,2)}
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {generatedPassword.slice(2,4)}
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {generatedPassword.slice(4,6)}
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {generatedPassword.slice(6,8)}
                </Typography>
              </Box>
              <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'}>
                <ContentCopyIcon />
              </IconButton>
            </Paper>

            <Alert severity="warning">
              <Typography variant="body2">
                {t('departments:form.save_password_warning')}
              </Typography>
            </Alert>

            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('departments:department_details')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('departments:labels.name')}:</strong> {createdDepartment?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('departments:labels.master_username')}:</strong> {formData?.master_username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>{t('departments:labels.modules_count')}:</strong> {selectedModules.length}
              </Typography>
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <FormPage title={t('departments:create_department')} onBack={handleCancel}>
      <FormSection>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </Box>

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button
            onClick={handleCancel}
            disabled={departmentsLoading}
          >
            {t('departments:actions.cancel')}
          </Button>
          
          <Box display="flex" gap={2}>
            {activeStep > 0 && activeStep < 2 && (
              <Button
                onClick={handleBack}
                disabled={departmentsLoading || activeStep === 1}
              >
                {t("common:buttons.back")}
              </Button>
            )}
            
            {activeStep < 2 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={departmentsLoading || modulesLoading}
              >
                {activeStep === 1 ? t('common:buttons.finish') : t('common:buttons.next')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleFinish}
              >
                {t('departments:actions.close')}
              </Button>
            )}
          </Box>
        </Box>
      </FormSection>
    </FormPage>
  );
}
